import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Container,
  Paper,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Navbar from '../../../../globals/components/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClient } from '../../../../api/httpClient';
import { useSelector } from 'react-redux';
import {
  buildInternalUserPayload,
  INTERNAL_USER_ENDPOINTS,
} from './internalUserFormConfig';
import { useInternalUserEmailCheck } from './useInternalUserEmailCheck';
import {
  InternalUserAccessFlags,
  InternalUserBasicFields,
  InternalUserEmailField,
  InternalUserFeaturePermissions,
  InternalUserPermissionLevelField,
} from './InternalUserFormFields';

const EditInternalUser = () => {
  const navigate = useNavigate();
  const { internal_user_id } = useParams();
  const [permissionLevel, setPermissionLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingResend, setLoadingResend] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });
  const [isPiiUser, setIsPiiUser] = useState(false);
  const [isCriticalUser, setIsCriticalUser] = useState(false);
  const [responseGlobal, setResponseGlobal] = useState(null);

  const userdetails = useSelector((state) => state.auth.user);
  const isCritical = userdetails?.is_critical_user;

  const {
    emailExists,
    emailChecking,
    emailDialogOpen,
    handleEmailChange,
    handleDialogClose,
    handleDialogContinue,
    handleDialogCancel,
  } = useInternalUserEmailCheck({
    onEmailClear: () => setFormData((prev) => ({ ...prev, email: '' })),
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await apiClient(
          INTERNAL_USER_ENDPOINTS.get(internal_user_id),
          'GET'
        );
        setResponseGlobal(response);
        if (response) {
          setFormData({
            firstName: response.user.first_name || '',
            lastName: response.user.last_name || '',
            phoneNumber: response.phonenumber || '',
            email: response.user.email || '',
          });
          setPermissionLevel(response.is_admin ? 'Admin' : 'User');
          setPermissions(response.features || []);
          setIsPiiUser(!!response.is_pii_user);
          setIsCriticalUser(!!response.is_critical_user);
        }
      } catch (error) {
        console.error('Error fetching user details:', error.message || error);
        toast.error(
          `Error fetching user details: ${error.message || 'Unknown error'}`
        );
      } finally {
        setLoadingData(false);
      }
    };
    fetchUserDetails();
  }, [internal_user_id]);

  const handleResendInvite = async (event) => {
    event.preventDefault();
    setLoadingResend(true);
    try {
      const response = await apiClient(
        INTERNAL_USER_ENDPOINTS.resendInvite(internal_user_id),
        'PATCH'
      );
      if (response) {
        toast.success('Internal User invite Resent successfully');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error updating user details:', error.message || error);
      toast.error(`Error updating user: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingResend(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') {
      handleEmailChange(value);
    }
  };

  const handlePermissionChange = (event) => {
    const { value, checked } = event.target;
    setPermissions((prevPermissions) =>
      checked
        ? [...prevPermissions, value]
        : prevPermissions.filter((perm) => perm !== value)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const payload = buildInternalUserPayload({
      formData,
      permissionLevel,
      permissions,
      isPiiUser,
      isCriticalUser,
    });

    try {
      const response = await apiClient(
        INTERNAL_USER_ENDPOINTS.update(internal_user_id),
        'PATCH',
        payload
      );
      if (response) {
        toast.success('Internal User updated successfully');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error updating user details:', error.message || error);
      toast.error(`Error updating user: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" style={{ marginTop: '3rem' }}>
        {loadingData ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ height: '100vh' }}
          >
            <CircularProgress size={50} />
          </Grid>
        ) : (
          <Paper
            elevation={3}
            style={{
              padding: '2rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '15px',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              style={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              Edit Internal User
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <InternalUserEmailField
                  formData={formData}
                  onInputChange={handleInputChange}
                  emailExists={emailExists}
                  emailChecking={emailChecking}
                  emailDialogOpen={emailDialogOpen}
                  onDialogClose={handleDialogClose}
                  onDialogContinue={handleDialogContinue}
                  onDialogCancel={handleDialogCancel}
                />
                <InternalUserBasicFields formData={formData} onInputChange={handleInputChange} />
                <InternalUserPermissionLevelField
                  permissionLevel={permissionLevel}
                  onChange={(e) => setPermissionLevel(e.target.value)}
                />
                <InternalUserAccessFlags
                  isCritical={isCritical}
                  isPiiUser={isPiiUser}
                  isCriticalUser={isCriticalUser}
                  onPiiChange={setIsPiiUser}
                  onCriticalChange={setIsCriticalUser}
                />
                <InternalUserFeaturePermissions
                  permissionLevel={permissionLevel}
                  permissions={permissions}
                  onPermissionChange={handlePermissionChange}
                />
                <Grid
                  size={{ xs: 12 }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '2rem',
                  }}
                >
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleCancel}
                    style={{ marginRight: '1rem' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={24} /> : null}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </Button>
                  {responseGlobal && responseGlobal.is_accepted !== null && (
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={loadingResend || responseGlobal.is_accepted === true}
                      onClick={handleResendInvite}
                      style={{ marginLeft: '1rem' }}
                      startIcon={loadingResend ? <CircularProgress size={24} /> : null}
                    >
                      {loadingResend ? 'Resending...' : 'Resend Invite'}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default EditInternalUser;
